import BulkSubBudgetEditor from "./BulkSubBudgetEditor";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BulkStepLayout from "../../pages/BulkStepLayout";
import { Table, TableBody, TableCell, TableContainer, TableRow, Paper, Button, Backdrop, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { useItems } from "../../features/Fetch/Items";
import { BudgetItem, CurrentUser, FamilyBucketLabel, TimeframeItem, TransactionItem, USCurrency, UserItem } from "../../types";

// budgets come from server — adding budgets removed. No local empty budget creation.

const BulkBudgetSubBudgetEditor = (props: any) => {
  const { step, onNext, onBack, steps } = props;
  const repo = useItems();
  const navigate = useNavigate();
  const budgets: BudgetItem[] = useSelector((state: any) => state.budgets.value);
  const transactions: TransactionItem[] = useSelector((state: any) => state.transactions.value);
  const checkRequests: TransactionItem[] = useSelector((state: any) => state.checkRequests.value);
  const timeframes: TimeframeItem[] = useSelector((state: any) => state.timeframes.value);
  const users: UserItem[] = useSelector((state: any) => state.users.value);
  const user: CurrentUser = useSelector((state: any) => state.userData.value);
  const subBudgetLabel = FamilyBucketLabel(user, "subBudget", "SubBudget");
  const [rows, setRows] = useState<BudgetItem[]>([]);
  const [originalRows, setOriginalRows] = useState<BudgetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirmExit, setShowConfirmExit] = useState(false);

  useEffect(() => {
    if (budgets && timeframes && users) {
      // Use only existing budgets; do not add an empty row
      setRows([...budgets]);
      setOriginalRows([...budgets]);
      setLoading(false);
    }
  }, [budgets, timeframes, users]);

  if (loading || !budgets || !timeframes || !users) return <p>Loading...</p>;

  return (
    <BulkStepLayout step={step} onNext={onNext} onBack={onBack} steps={steps}>
      <Paper elevation={0} style={{ padding: 8, backgroundColor: 'azure', paddingBottom: 64 }}>
        <h2 className="bulk-category-title">Bulk {subBudgetLabel} Editor</h2>
        <TableContainer style={{overflow: "unset"}}>
          <Table size="small" className="fixed-width-table">
            <TableBody>
              {rows.map((row, idx) => (
                <React.Fragment key={idx}>
                  <TableRow>
                    <TableCell colSpan={7} className="bottomless">
                      <BulkSubBudgetEditor
                        budgetName={row.description}
                        budgetAmount={USCurrency(row.variant.amount)}
                        budgetIdx={idx}
                        row={row}
                        transactions={transactions}
                        checkRequests={checkRequests}
                        handleSubBudgetChange={handleSubBudgetChange}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow><TableCell className="bottomless"></TableCell></TableRow>
                  <TableRow><TableCell className="bottomless"></TableCell></TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <div className="bulk-budget-action-row">
          <Button
            variant="outlined"
            color="error"
            onClick={handleExitWithoutSaving}
            style={{ minWidth: 160, padding: '4px 16px' }}
          >
            Back
          </Button>
          <div className="bulk-budget-action-buttons">
            {hasChangesToSave() && (
              <Button
                variant="contained"
                color="success"
                onClick={handleSaveAndExit}
                style={{ minWidth: 120, padding: '4px 16px' }}
              >
                Save and Exit
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={hasChangesToSave() ? handleSaveAndContinue : onNext}
              style={{ minWidth: 120, padding: '4px 16px' }}
            >
              {hasChangesToSave() ? 'Save and Continue' : 'Continue'}
            </Button>
          </div>
        </div>
        {showConfirmExit && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Go Back Without Saving?</h3>
              <p>Are you sure you want to go back without saving your changes? All changes on this page will be lost.</p>
              <div className="modal-actions">
                <Button variant="contained" color="error" onClick={handleConfirmExit}>Go Back</Button>
                <Button variant="outlined" onClick={handleCancelExit}>Cancel</Button>
              </div>
            </div>
          </div>
        )}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading || saving}>
        <CircularProgress />
      </Backdrop>
      </Paper>
    </BulkStepLayout>
  );

  function handleSubBudgetChange(budgetIdx: number, budget: BudgetItem) {
    const updatedRows = rows.map((row, i) => {
      if (i !== budgetIdx) return row;
      return budget;
    });
    setRows(updatedRows);
  }

  function isRowEmpty(row: BudgetItem) {
    return !row.description && !row.variant.amount && !row.timeframeId;
  }

  function hasChanged(row: BudgetItem) {
    const original = originalRows.find(r => r.budgetId === row.budgetId);
    return original && JSON.stringify(original) !== JSON.stringify(row);
  }

  function hasChangesToSave() {
    return rows.some(row => {
      if (!row.budgetId && !isRowEmpty(row)) return true; // new row (unlikely now)
      if (row.budgetId && !isRowEmpty(row) && hasChanged(row)) return true; // changed row
      return false;
    });
  }

  async function handleSave() {
    setSaving(true);
    const updatedBudgets = rows.filter(row => row.budgetId && !isRowEmpty(row) && hasChanged(row));
    const newBudgets = rows.filter(row => !row.budgetId && !isRowEmpty(row));

    // Use bulk upsert API for new and updated budgets
    await repo.UpsertBudgetsAsync([...updatedBudgets, ...newBudgets]);

    // Reload all after save
    await repo.GetBudgetsAsync();
    setSaving(false);
  }

  async function handleSaveAndContinue() {
    await handleSave();
    onNext();
  }

  async function handleSaveAndExit() {
    await handleSave();
    navigate('/budgets');
  }

  function handleExitWithoutSaving() {
    if (hasChangesToSave()) {
      setShowConfirmExit(true);
    } else {
      onBack();
    }
  }

  function handleConfirmExit() {
    setShowConfirmExit(false);
    onBack();
  }

  function handleCancelExit() {
    setShowConfirmExit(false);
  }
};

export default BulkBudgetSubBudgetEditor;
